import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import Modal from '../components/Modal';
import { apiErrorCode } from '../utils/apiError';
import { useAuth } from '../context/AuthContext';
import { personApi, lawyerApi, inspectorApi } from '../services/api';
import { contrastAgainstWhite, isHexColor, type CompanyTheme } from '../components/company-theme/companyTheme';
import { useThemeLogo } from '../components/company-theme/useThemeLogo';
import { FoldedPresentation } from '../components/FoldedPresentation';

interface CompanyInfo {
  companyNumber: number;
  companyName: string;
  companyPresentation: string | null;
  website?: string;
  email?: string;
  phone?: string;
  organizationNumber?: string | null;
  address?: string | null;
  postalCode?: string | null;
  city?: string | null;
  experienceYears?: number;
  specialties?: string[];
  languages?: string[];
  geographicArea?: string;
  hourlyRate?: string;
  responseTime?: string;
  leadTime?: string;
  isSystemCompany?: boolean;
  /** The company's own theme; null or standard keeps the plain card. */
  theme?: CompanyTheme | null;
  projects?: Array<{
    projectId: string;
    projectNumber: number;
    description: string;
  }>;
}

const initials = (name: string) => name.split(' ').map(part => part[0]).join('').substring(0, 2).toUpperCase();

/** The company's logo when it has one (and it loads), else its initials. */
function CompanyMark({ company }: { company: CompanyInfo }) {
  const logoUrl = company.theme && !company.theme.isDefault ? company.theme.logoUrl : null;
  const { src, failed } = useThemeLogo(logoUrl);
  if (src && !failed) {
    return (
      <span className="company-card-logo">
        <img src={src} alt={company.companyName} />
      </span>
    );
  }
  return (
    <div className="avatar-text rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 56, height: 56, fontSize: 18, fontWeight: 600 }} aria-hidden="true">
      {initials(company.companyName)}
    </div>
  );
}

/**
 * The company's accent as a stripe on the card, and as the link colour when it
 * is readable on white (4.5:1), else the standard colour (ClickUp 869fgp108).
 */
const themeStyle = (theme?: CompanyTheme | null): React.CSSProperties | undefined => {
  if (!theme || theme.isDefault || !isHexColor(theme.accentColor)) return undefined;
  return {
    '--company-accent': theme.accentColor,
    ...(contrastAgainstWhite(theme.accentColor) >= 4.5 ? { '--company-link': theme.accentColor } : {}),
  } as React.CSSProperties;
};


const PROJECTS_SHOWN = 4;

const websiteHref = (website: string) => (/^https?:\/\//i.test(website) ? website : `https://${website}`);

/**
 * One builder the person works with: logo, name and how to reach them on top,
 * the presentation and specialties as the main text, and the practical facts
 * and the person's own projects beside it (below it on phones). The company's
 * theme only sets the logo and the accent (ClickUp 869fgp108).
 */
function CompanyCard({ company, onRemove }: { company: CompanyInfo; onRemove?: (company: CompanyInfo) => void }) {
  const { t } = useTranslation();
  const style = themeStyle(company.theme);
  const place = [company.postalCode, company.city].filter(Boolean).join(' ');
  const address = [company.address, place].filter(Boolean).join(', ');
  const facts: Array<{ icon: string; label: string; value: React.ReactNode }> = [
    ...((company.experienceYears || 0) > 0 ? [{ icon: 'award', label: t('marketplace.provider.experience', 'Erfarenhet'), value: t('myCompanies.yearsValue', { years: company.experienceYears }) }] : []),
    ...(company.hourlyRate ? [{ icon: 'tag', label: t('marketplace.provider.hourlyRate', 'Timarvode'), value: company.hourlyRate }] : []),
    ...(company.responseTime ? [{ icon: 'clock', label: t('marketplace.provider.responseTime', 'Svarstid'), value: company.responseTime }] : []),
    ...(company.leadTime ? [{ icon: 'calendar', label: t('marketplace.provider.leadTime', 'Ledtid'), value: company.leadTime }] : []),
    ...(company.geographicArea ? [{ icon: 'map-pin', label: t('marketplace.provider.geographicArea', 'Område'), value: company.geographicArea }] : []),
    ...((company.languages || []).length > 0 ? [{ icon: 'globe', label: t('marketplace.provider.languages', 'Språk'), value: (company.languages || []).join(', ') }] : []),
    ...(address ? [{ icon: 'home', label: t('myCompanies.address'), value: address }] : []),
    ...(company.organizationNumber ? [{ icon: 'file-text', label: t('myCompanies.organizationNumberShort'), value: company.organizationNumber }] : []),
  ];
  const projects = company.projects || [];
  const specialties = company.specialties || [];
  // A few projects at a time, so a long-standing builder's card stays short.
  const [allProjects, setAllProjects] = useState(false);
  const shownProjects = allProjects ? projects : projects.slice(0, PROJECTS_SHOWN);

  return (
    <article
      className={`card mb-4 company-card${style ? ' is-themed' : ''}`}
      style={style}
      data-testid={`company-card-${company.companyNumber}`}
    >
      <div className="card-body company-card-body">
        <header className="company-card-head">
          <CompanyMark company={company} />
          <div className="company-card-title">
            <h2 className="company-card-name">{company.companyName}</h2>
            {(company.city || company.organizationNumber) && (
              <p className="company-card-meta">
                {[company.city, company.organizationNumber && t('myCompanies.orgShort', { number: company.organizationNumber })].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          {(company.phone || company.email || company.website) && (
            <nav className="company-card-contact" aria-label={t('myCompanies.contact', { name: company.companyName })}>
              {company.phone && (
                <a className="company-card-action" href={`tel:${company.phone.replace(/\s/g, '')}`}>
                  <i className="feather-phone" aria-hidden="true"></i>{company.phone}
                </a>
              )}
              {company.email && (
                <a className="company-card-action" href={`mailto:${company.email}`}>
                  <i className="feather-mail" aria-hidden="true"></i>{company.email}
                </a>
              )}
              {company.website && (
                <a className="company-card-action" href={websiteHref(company.website)} target="_blank" rel="noopener noreferrer">
                  <i className="feather-external-link" aria-hidden="true"></i>{t('myCompanies.website')}
                </a>
              )}
            </nav>
          )}
        </header>

        <div className="company-card-grid">
          <div className="company-card-main">
            <section>
              <h3 className="company-card-heading">{t('settings.companyDetails.companyPresentation', 'Företagspresentation')}</h3>
              {company.companyPresentation
                ? <FoldedPresentation html={company.companyPresentation} />
                : <p className="text-muted mb-0">{t('personHome.noPresentation', 'Ingen presentation tillagd ännu.')}</p>}
            </section>
            {specialties.length > 0 && (
              <section>
                <h3 className="company-card-heading">{t('marketplace.provider.specialties', 'Specialistområden')}</h3>
                <ul className="company-card-tags">
                  {specialties.map(specialty => <li key={specialty}>{specialty}</li>)}
                </ul>
              </section>
            )}
          </div>

          {(facts.length > 0 || projects.length > 0) && (
            <aside className="company-card-side">
              {facts.length > 0 && (
                <section>
                  <h3 className="company-card-heading">{t('marketplace.provider.practicalInfo', 'Praktisk information')}</h3>
                  <dl className="company-card-facts">
                    {facts.map(fact => (
                      <div key={fact.label}>
                        <dt><i className={`feather-${fact.icon}`} aria-hidden="true"></i>{fact.label}</dt>
                        <dd>{fact.value}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              )}
              {projects.length > 0 && (
                <section>
                  <h3 className="company-card-heading">{t('myCompanies.yourProjects')}</h3>
                  <ul className="company-card-projects">
                    {shownProjects.map(project => (
                      <li key={project.projectId}>
                        <Link to={`/person/projects/${project.projectId}`}>
                          <i className="feather-folder" aria-hidden="true"></i>
                          <span className="company-card-project-number">{t('myCompanies.projectLabel', { number: project.projectNumber })}</span>
                          {project.description && <span className="company-card-project-name">{project.description}</span>}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  {projects.length > PROJECTS_SHOWN && (
                    <button type="button" className="company-card-projects-more" aria-expanded={allProjects}
                      onClick={() => setAllProjects(value => !value)}>
                      {allProjects ? t('myCompanies.fewerProjects') : t('myCompanies.allProjects', { count: projects.length })}
                    </button>
                  )}
                </section>
              )}
            </aside>
          )}
        </div>
        {/* The client can remove a contractor without projects (ClickUp 869fj4txv). */}
        {onRemove && projects.length === 0 && (
          <div className="d-flex justify-content-end mt-3">
            <button type="button" className="btn btn-outline-danger customer-remove-action" onClick={() => onRemove(company)}>
              <i className="feather-user-x me-2" aria-hidden="true"></i>
              {t('myCompanies.remove.action')}
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

export function MyCompanies() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [removing, setRemoving] = useState<CompanyInfo | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const canRemove = user?.personaType === 'person';

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCompanies = async () => {
    try {
      let data: CompanyInfo[] = [];
      if (user?.personaType === 'person') {
        data = await personApi.getLinkedCompanyInfo();
      } else if (user?.personaType === 'lawyer') {
        data = await lawyerApi.getLinkedCompanies();
      } else if (user?.personaType === 'inspector') {
        data = await inspectorApi.getLinkedCompanies();
      }
      // Filter out system company for persons
      setCompanies(data.filter(c => !c.isSystemCompany));
    } catch (error) {
      console.error('Failed to load companies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!removing) return;
    try {
      setIsRemoving(true);
      await personApi.removeCompany(removing.companyNumber);
      setCompanies(current => current.filter(company => company.companyNumber !== removing.companyNumber));
      setMessage({ type: 'success', text: t('myCompanies.remove.success', { name: removing.companyName }) });
    } catch (error) {
      setMessage({
        type: 'error',
        text: apiErrorCode(error) === 'COMPANY_HAS_PROJECTS' ? t('myCompanies.remove.hasProjects') : t('myCompanies.remove.error')
      });
    } finally {
      setIsRemoving(false);
      setRemoving(null);
    }
  };

  // The person's builders; lawyers and inspectors keep "Företag".
  const title = user?.personaType === 'person'
    ? t('myCompanies.titlePerson')
    : t('nav.companies', 'Hantverkare');

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{title}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: title }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('myCompanies.description', 'Hantverkare du är kopplad till')}</p>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'}`} role={message.type === 'error' ? 'alert' : 'status'}>
          {message.text}
        </div>
      )}
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      ) : companies.length === 0 ? (
        <Card>
          <div className="card-body text-center py-5">
            <i className="feather-briefcase text-muted mb-3" style={{ fontSize: 48 }}></i>
            <p className="text-muted mb-0">{t('myCompanies.noCompanies', 'Du är inte kopplad till någon hantverkare ännu.')}</p>
          </div>
        </Card>
      ) : (
        companies.map((company) => (
          <CompanyCard key={company.companyNumber} company={company} onRemove={canRemove ? setRemoving : undefined} />
        ))
      )}

      {removing && (
        <Modal
          title={t('myCompanies.remove.title')}
          onClose={() => setRemoving(null)}
          closeAriaLabel={t('common.close')}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setRemoving(null)} disabled={isRemoving}>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-danger" onClick={() => void handleRemove()} disabled={isRemoving} data-testid="company-remove-confirm">
                {isRemoving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                {t('myCompanies.remove.confirm')}
              </button>
            </>
          }
        >
          <p className="mb-0">{t('myCompanies.remove.body', { name: removing.companyName })}</p>
        </Modal>
      )}
    </DashboardLayout>
  );
}
